/** React */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

/** MUI icons */
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

/** MUI */
import { styled } from "@mui/material/styles";
import { Box, Card, CardContent, Chip, Grid, Typography } from "@mui/material";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";

/** Component styles */
const PREFIX = "ControllerSummary";

const classes = {
    cardContentRoot: `${PREFIX}-cardContentRoot`,
    summaryLabels: `${PREFIX}-summaryLabels`,
    summaryDetails: `${PREFIX}-summaryDetails`,
};

const StyledContainer = styled(Box)(({ theme }) => ({
    [`& .${classes.cardContentRoot}`]: {
        padding: "24px 30px 30px !important",
    },

    [`& .${classes.summaryLabels}`]: {
        display: "flex",
        justifyContent: "space-between",
        padding: "24px 0px 8px",
        borderBottom: "1px solid #cccc",
    },

    [`& .${classes.summaryDetails}`]: {
        display: "flex",
        justifyContent: "space-between",
        paddingLeft: "16px",
    },
}));

const Accordion = styled((props) => <MuiAccordion disableGutters elevation={0} square {...props} />)({
    boxShadow: "none",
    "&:before": {
        display: "none",
    },
});

const AccordionSummary = styled((props) => (
    <MuiAccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#031851" }} />} {...props} />
))({
    "& .MuiAccordionSummary-content": {
        margin: "12px 0",
    },
});

const AccordionDetails = styled(MuiAccordionDetails)({
    padding: "0px 0px 20px 0px",
    "& .MuiCard-root": {
        boxShadow: "none",
    },
});

export const ControllerSummary = (props) => {
    /** Properties */
    const { valuesContRecu, valuesContCatchUp, valuesContOnboard, valuesContConsul } = props;

    /** Internationalization i18n */
    const [t] = useTranslation("global");

    /** React redux */
    const todos = useSelector((state) => state.todos);

    /** Component states */
    const [expanded, setExpanded] = useState(true);

    /** Component functions */
    const handleChangeSumaryAccordion = (event, isExpanded) => {
        setExpanded(isExpanded);
    };

    return (
        <StyledContainer>
            <Card
                elevation={0}
                sx={{
                    mt: 3,
                    borderRadius: 8,
                }}
            >
                <CardContent sx={{ pt: 1 }}>
                    <Accordion
                        expanded={expanded}
                        onChange={handleChangeSumaryAccordion}
                        TransitionProps={{ unmountOnExit: true }}
                    >
                        <AccordionSummary
                            aria-controls="panel-content"
                            id="panel-header"
                            sx={{
                                borderBottom: "2px solid #ff5968",
                            }}
                        >
                            <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                                <Grid item>
                                    <Typography
                                        variant="h2"
                                        align="center"
                                        sx={{
                                            fontWeight: 700,
                                        }}
                                    >
                                        Controller
                                    </Typography>
                                </Grid>
                                <Grid item sx={{ pr: 1 }}>
                                    {!valuesContRecu.payAnnually && (
                                        <Typography variant="h3" sx={{ pb: 0.5 }}>
                                            {`$${parseFloat(
                                                !valuesContRecu.chip
                                                    ? 0
                                                    : valuesContRecu.recuProjectTotal +
                                                          valuesContRecu.recuHours *
                                                              valuesContRecu.recuHourRate *
                                                              (1 - valuesContRecu.recuDiscount / 100)
                                            ).toLocaleString(todos.amountFormat, {
                                                maximumFractionDigits: 2,
                                            })}/${t(`proposals.month`)}`}
                                        </Typography>
                                    )}
                                    <Typography variant="h3">
                                        {`$${parseFloat(
                                            (!valuesContOnboard.chip ? 0 : valuesContOnboard.onboardProjectTotal) +
                                                (!valuesContCatchUp.chip
                                                    ? 0
                                                    : valuesContCatchUp.projectTotal +
                                                      valuesContCatchUp.totalHours *
                                                          valuesContCatchUp.hourRate *
                                                          (1 - valuesContCatchUp.discount / 100)) +
                                                (!valuesContRecu.chip || !valuesContRecu.payAnnually
                                                    ? 0
                                                    : valuesContRecu.recuProjectTotal +
                                                      valuesContRecu.recuHours *
                                                          valuesContRecu.recuHourRate *
                                                          (1 - valuesContRecu.recuDiscount / 100)) +
                                                (!valuesContConsul.chip ? 0 : valuesContConsul.consulProjectTotal)
                                        ).toLocaleString(todos.amountFormat, {
                                            maximumFractionDigits: 2,
                                        })}/${t(`proposals.oneTime`)}`}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </AccordionSummary>
                        <AccordionDetails>
                            {valuesContOnboard.chip && (
                                <Grid container justifyContent="flex-end" spacing={2}>
                                    <Grid item xs={11}>
                                        <Box className={classes.summaryLabels}>
                                            <Typography
                                                variant="h2"
                                                align="center"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: valuesContOnboard.isError? "#ff5968": "primary"
                                                }}
                                            >
                                                {t(`invoicing.onboarding`)}
                                            </Typography>
                                            <Chip
                                                id="hour-addons-chip"
                                                label={
                                                    <Typography variant="h3" align="center">
                                                        {`$${parseFloat(
                                                            valuesContOnboard.onboardProjectTotal
                                                        ).toLocaleString(todos.amountFormat, {
                                                            maximumFractionDigits: 2,
                                                        })}/${t(`proposals.oneTime`)}`}
                                                    </Typography>
                                                }
                                                sx={{
                                                    //backgroundColor: "#2f3190",
                                                    backgroundColor: valuesContOnboard.isError? "#ff5968": "#2f3190",
                                                    color: "#fff",
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                </Grid>
                            )}
                            {valuesContCatchUp.chip && (
                                <Grid container justifyContent="flex-end" spacing={2}>
                                    <Grid item xs={11}>
                                        <Box className={classes.summaryLabels}>
                                            <Typography
                                                variant="h2"
                                                align="center"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: valuesContCatchUp.isError? "#ff5968": "primary"
                                                }}
                                            >
                                                {t(`invoicing.catchUp`)}
                                            </Typography>
                                            <Chip
                                                id="hour-addons-chip"
                                                label={
                                                    <Typography variant="h3" align="center">
                                                        {`$${parseFloat(
                                                            valuesContCatchUp.projectTotal +
                                                                valuesContCatchUp.totalHours *
                                                                    valuesContCatchUp.hourRate *
                                                                    (1 - valuesContCatchUp.discount / 100)
                                                        ).toLocaleString(todos.amountFormat, {
                                                            maximumFractionDigits: 2,
                                                        })}/${t(`proposals.oneTime`)}`}
                                                    </Typography>
                                                }
                                                sx={{
                                                    //backgroundColor: "#2f3190",
                                                    backgroundColor: valuesContCatchUp.isError? "#ff5968": "#2f3190",
                                                    color: "#fff",
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item container justifyContent="flex-end" spacing={1}>
                                        <Grid item xs={11}>
                                            <Box className={classes.summaryDetails}>
                                                <Typography
                                                    variant="h3"
                                                    align="center"
                                                    sx={{
                                                        display: "block",
                                                        mt: "auto",
                                                        mb: "auto",
                                                        fontWeight: 700,
                                                        color: valuesContCatchUp.isError? "#ff5968": "primary"
                                                    }}
                                                >
                                                    {`${t("proposals.basicService")} (${valuesContCatchUp.months} ${t(
                                                        "proposals.months"
                                                    )})`}
                                                </Typography>
                                                <>
                                                    <Typography
                                                        variant="body2"
                                                        align="center"
                                                        sx={{
                                                            display: "block",
                                                            mt: "auto !important",
                                                            mb: "auto !important",
                                                            color: valuesContCatchUp.isError? "#ff5968": "primary"
                                                        }}
                                                    >
                                                        {`$${parseFloat(valuesContCatchUp.projectTotal).toLocaleString(
                                                            todos.amountFormat,
                                                            {
                                                                maximumFractionDigits: 2,
                                                            }
                                                        )}/${t("proposals.oneTime")}`}
                                                    </Typography>
                                                </>
                                            </Box>
                                        </Grid>
                                        {valuesContCatchUp.catchUpChecks && (
                                            <Grid item xs={11}>
                                                <Box className={classes.summaryDetails}>
                                                    <Typography
                                                        variant="h3"
                                                        align="center"
                                                        sx={{
                                                            display: "block",
                                                            mt: "auto",
                                                            mb: "auto",
                                                            fontWeight: 700,
                                                            color: valuesContCatchUp.isError? "#ff5968": "primary"
                                                        }}
                                                    >
                                                        {`${t(`proposals.addons`)} (${
                                                            valuesContCatchUp.totalHours
                                                        } hrs)`}
                                                    </Typography>
                                                    <Typography variant="body2" align="center" sx={{color: valuesContCatchUp.isError? "#ff5968": "primary"}}>
                                                        {`$${parseFloat(
                                                            valuesContCatchUp.totalHours *
                                                                valuesContCatchUp.hourRate *
                                                                (1 - valuesContCatchUp.discount / 100)
                                                        ).toLocaleString(todos.amountFormat, {
                                                            maximumFractionDigits: 2,
                                                        })}/${t(`proposals.oneTime`)}`}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Grid>
                            )}
                            {valuesContRecu.chip && (
                                <Grid container justifyContent="flex-end" spacing={2}>
                                    <Grid item xs={11}>
                                        <Box className={classes.summaryLabels}>
                                            <Typography
                                                variant="h2"
                                                align="center"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: valuesContRecu.isError? "#ff5968": "primary"
                                                }}
                                            >
                                                {`${t(`invoicing.recurring`)}${valuesContRecu.payAnnually ? "**" : ""}`}
                                            </Typography>
                                            <Chip
                                                id="hour-addons-chip"
                                                label={
                                                    <Typography variant="h3" align="center">
                                                        {`$${parseFloat(
                                                            valuesContRecu.recuProjectTotal +
                                                                valuesContRecu.recuHours *
                                                                    valuesContRecu.recuHourRate *
                                                                    (1 - valuesContRecu.recuDiscount / 100)
                                                        ).toLocaleString(todos.amountFormat, {
                                                            maximumFractionDigits: 2,
                                                        })}/${
                                                            valuesContRecu.payAnnually
                                                                ? t(`proposals.oneTime`)
                                                                : t(`proposals.month`)
                                                        }`}
                                                    </Typography>
                                                }
                                                sx={{
                                                    //backgroundColor: "#2f3190",
                                                    backgroundColor: valuesContRecu.isError? "#ff5968": "#2f3190",
                                                    color: "#fff",
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item container justifyContent="flex-end" spacing={1}>
                                        <Grid item xs={11}>
                                            <Box className={classes.summaryDetails}>
                                                <Typography
                                                    variant="h3"
                                                    align="center"
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: valuesContRecu.isError? "#ff5968": "primary"
                                                    }}
                                                >
                                                    {t(`proposals.basicService`)}
                                                </Typography>
                                                <Typography variant="body2" align="center" sx={{color: valuesContRecu.isError? "#ff5968": "primary"}}>
                                                    {`$${parseFloat(valuesContRecu.recuProjectTotal).toLocaleString(
                                                        todos.amountFormat,
                                                        {
                                                            maximumFractionDigits: 2,
                                                        }
                                                    )}/${
                                                        valuesContRecu.payAnnually
                                                            ? t(`proposals.oneTime`)
                                                            : t(`proposals.month`)
                                                    }`}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        {valuesContRecu.recuChecks && (
                                            <Grid item xs={11}>
                                                <Box className={classes.summaryDetails}>
                                                    <Typography
                                                        variant="h3"
                                                        align="center"
                                                        sx={{
                                                            display: "block",
                                                            mt: "auto",
                                                            mb: "auto",
                                                            fontWeight: 700,
                                                            color: valuesContRecu.isError? "#ff5968": "primary"
                                                        }}
                                                    >
                                                        {`${t(`proposals.addons`)} (${valuesContRecu.recuHours} hrs)*`}
                                                    </Typography>
                                                    <Typography variant="body2" align="center" sx={{color: valuesContRecu.isError? "#ff5968": "primary"}}>
                                                        {`$${parseFloat(
                                                            valuesContRecu.recuHourRate *
                                                                valuesContRecu.recuHours *
                                                                (1 - valuesContRecu.recuDiscount / 100)
                                                        ).toLocaleString(todos.amountFormat, {
                                                            maximumFractionDigits: 2,
                                                        })}/${t("proposals.month")}`}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Grid>
                            )}
                            {valuesContConsul.chip && (
                                <Grid container justifyContent="flex-end" spacing={2}>
                                    <Grid item xs={11}>
                                        <Box className={classes.summaryLabels}>
                                            <Typography
                                                variant="h2"
                                                align="center"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: valuesContConsul.isError? "#ff5968": "primary"
                                                }}
                                            >
                                                {t(`invoicing.consulting`)}
                                            </Typography>
                                            <Chip
                                                id="hour-addons-chip"
                                                label={
                                                    <Typography variant="h3" align="center">
                                                        {`$${parseFloat(
                                                            valuesContConsul.consulProjectTotal
                                                        ).toLocaleString(todos.amountFormat, {
                                                            maximumFractionDigits: 2,
                                                        })}/${t(`proposals.oneTime`)}`}
                                                    </Typography>
                                                }
                                                sx={{
                                                    //backgroundColor: "#2f3190",
                                                    backgroundColor: valuesContConsul.isError? "#ff5968": "#2f3190",
                                                    color: "#fff",
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item container justifyContent="flex-end" spacing={1}>
                                        <Grid item xs={11}>
                                            <Box className={classes.summaryDetails}>
                                                <Typography
                                                    variant="h3"
                                                    align="center"
                                                    sx={{
                                                        display: "block",
                                                        mt: "auto",
                                                        mb: "auto",
                                                        fontWeight: 700,
                                                        color: valuesContConsul.isError? "#ff5968": "primary"
                                                    }}
                                                >
                                                    {`${t(`proposals.query`)} (${valuesContConsul.consulHours} hrs)`}
                                                </Typography>
                                                <Typography variant="body2" align="center" sx={{color: valuesContConsul.isError? "#ff5968": "primary"}}>
                                                    {`$${parseFloat(valuesContConsul.consulProjectTotal).toLocaleString(
                                                        todos.amountFormat,
                                                        {
                                                            maximumFractionDigits: 2,
                                                        }
                                                    )}/${t(`proposals.oneTime`)}`}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                        </AccordionDetails>
                    </Accordion>
                </CardContent>
            </Card>
        </StyledContainer>
    );
};
